import { Injectable, makeStateKey, TransferState } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { addProductsToListUseCase } from '../application/use-cases/add-products-to-list.use-case';
import { ProductLoaderOrigin } from '../domain/enums/product-loader-origin.enum';
import { ProductLoaderPort } from '../domain/ports/primary/product-loader.port';
import { Page } from '../models/page.interface';
import { LoadProductsAction } from '../store/product.actions';

import { ProductPaginationService } from './product-pagination.service';

import { ProductPublicModels } from '@features/products/product';

@Injectable({
    providedIn: 'root',
})
export class ProductLoaderService implements ProductLoaderPort {
    private readonly productRequest$: Subject<any> = new Subject();

    private currentCategoryId: number;

    private readonly stateKey = makeStateKey<any>('category-products');

    constructor(
        private readonly store: Store,
        private readonly productPaginationService: ProductPaginationService,
        private readonly transferState: TransferState,
    ) {
        this.productRequest$
            .pipe(
                filter((page) => !!page)
            )
            .subscribe((page) => {
                if (!page.visited) {
                    this.productPaginationService.markPageAsVisited(page);
                    this.store.dispatch(LoadProductsAction({ ids: page.ids.toString(), categoryId: `${this.currentCategoryId}` }));
                }
            });
    }

    public addApiProductsToList(productsApi: ProductPublicModels.PublicApiProduct[]): void {
        const productsArray = Object.values(productsApi);
        const tsProducts = this.transferState.get<any>(this.stateKey, []);
        this.transferState.set(this.stateKey, [...tsProducts, ...productsArray]);
    }

    public getApiProductsList(productIds: string[]): ProductPublicModels.PublicApiProduct[] {
        const tsProducts = this.transferState.get<any>(this.stateKey, []);
        if (!tsProducts) return [];

        const objProductIds = Object.fromEntries(tsProducts.map(product => [product.id.toString(), product]));
        return productIds.map(id => objProductIds[id]).filter(Boolean);
    }

    public addProductsToList(productsApi: ProductPublicModels.PublicProduct[], origin?: ProductLoaderOrigin): void {
        addProductsToListUseCase(this.store, this.productPaginationService, productsApi, origin);
    }

    public initPagination(productsIds: number[], categoryId: number): void {
        this.currentCategoryId = categoryId;

        this.productPaginationService.initCategoryProducts(productsIds, categoryId);

        if (productsIds && productsIds.length !== 0) {
            const page: Page = this.productPaginationService.getPageByProduct(productsIds[0].toString());
            this.productRequest$.next(page);
        }
    }

    public requestProduct(productId: number): void {
        const page: Page = this.productPaginationService.getPageByProduct(productId.toString());
        if (!!page && !page?.visited) {
            this.productRequest$.next(page);
        }
    }
}
